import { useState, useEffect } from 'react';
import { Dialog, DialogPanel, Title, Text, TextInput, Button, Flex } from '@tremor/react';
import { api } from '../../lib/api';

interface ResetAllModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function ResetAllModal({ isOpen, onClose, onSuccess }: ResetAllModalProps) {
  const [confirmation, setConfirmation] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{
    queues: { keysToDelete: number; prefix: string };
    idempotency: { processedDocumentsToCllear: number };
    rateLimiterMetrics: { note: string };
    mongodb: { note: string };
  } | null>(null);

  // Load preview when modal opens
  useEffect(() => {
    if (isOpen) {
      setConfirmation('');
      setError(null);
      loadPreview();
    }
  }, [isOpen]);

  const loadPreview = async () => {
    setIsLoadingPreview(true);
    try {
      const data = await api.resetAllPreview();
      setPreview(data.preview);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load preview');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleReset = async () => {
    if (confirmation !== 'RESET') {
      setError('You must type RESET to confirm');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await api.resetAll('RESET');
      if (result.success) {
        onSuccess();
        onClose();
      } else {
        setError('Reset completed with some errors - check console');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset system');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setConfirmation('');
      setError(null);
      setPreview(null);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} static={true}>
      <DialogPanel className="bg-gray-900 border border-red-900 max-w-lg">
        {/* Warning Header */}
        <div className="bg-red-950/50 border border-red-900 rounded-lg p-4 mb-4">
          <div className="text-center mb-2">
            <span className="text-2xl">&#9888;</span>
          </div>
          <Title className="text-red-400 text-center">DANGER: Full System Reset</Title>
        </div>

        {/* Warning Message */}
        <div className="bg-red-950/30 border border-red-900/50 rounded p-3 mb-4">
          <Text className="text-red-300 font-medium mb-2">
            This will DELETE/RESET the following data:
          </Text>
          <ul className="text-red-200 text-sm space-y-1 ml-4">
            <li>All BullMQ queue data (jobs, history)</li>
            <li>Idempotency tracking (processed documents list)</li>
            <li>Rate limiter metrics (in-memory stats)</li>
          </ul>
          <Text className="text-yellow-400 text-sm mt-3">
            Note: MongoDB workflow records are NOT affected
          </Text>
          <Text className="text-red-400 font-bold mt-2">
            This action CANNOT be undone!
          </Text>
        </div>

        {/* Preview */}
        {isLoadingPreview ? (
          <div className="bg-gray-800/50 rounded p-3 mb-4">
            <Text className="text-gray-400">Loading preview...</Text>
          </div>
        ) : preview ? (
          <div className="bg-gray-800/50 rounded p-3 mb-4 space-y-2">
            <Flex justifyContent="between">
              <Text className="text-gray-400">Queue keys to delete:</Text>
              <Text className="text-red-400 font-bold">{preview.queues.keysToDelete.toLocaleString()}</Text>
            </Flex>
            <Flex justifyContent="between">
              <Text className="text-gray-400">Processed documents to clear:</Text>
              <Text className="text-red-400 font-bold">{preview.idempotency.processedDocumentsToCllear.toLocaleString()}</Text>
            </Flex>
            <div className="pt-2 border-t border-gray-700">
              <Text className="text-gray-500 text-xs">{preview.rateLimiterMetrics.note}</Text>
              <Text className="text-gray-500 text-xs mt-1">{preview.mongodb.note}</Text>
            </div>
          </div>
        ) : null}

        {/* Confirmation Input */}
        <div className="mb-4">
          <Text className="text-gray-300 mb-2">
            Type <span className="text-red-400 font-bold">RESET</span> to confirm:
          </Text>
          <TextInput
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            placeholder="Type RESET to confirm"
            className="bg-gray-800 border-gray-700"
            disabled={isLoading}
          />
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-950/30 border border-red-900/50 rounded p-2 mb-4">
            <Text className="text-red-400 text-sm">{error}</Text>
          </div>
        )}

        {/* Actions */}
        <Flex justifyContent="end" className="gap-3">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleReset}
            loading={isLoading}
            disabled={confirmation !== 'RESET' || isLoading}
          >
            Reset Everything
          </Button>
        </Flex>
      </DialogPanel>
    </Dialog>
  );
}
